import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Card, Button } from '@/components/ui'
import { Header, Footer } from '@/components/layout'

export default function Failed() {
  const { shortCode } = useParams<{ shortCode: string }>()
  const [searchParams] = useSearchParams()
  const errorMessage = searchParams.get('error')
  const responseCode = searchParams.get('code')

  const getErrorDescription = (code: string | null): string => {
    const errorCodes: Record<string, string> = {
      '-1': 'Transaction rejected',
      '-2': 'Transaction must be reversed',
      '-3': 'Error in transaction',
      '-4': 'Rejected transaction',
      '-5': 'Rejected by transbank',
      '-6': 'Transaction timeout',
      '-7': 'Invalid card',
      '-8': 'Processor error',
    }
    return code ? errorCodes[code] || 'Unknown error' : 'Transaction declined'
  }

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <Header />

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-8 md:py-12">
        <div className="fade-in">
          <Card variant="elevated" padding="lg">
            <div className="text-center py-8">
              {/* Error Icon */}
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-red-700 mb-3">Payment Failed</h3>

              {/* Message */}
              <p className="text-neutral-600 max-w-md mx-auto mb-4">
                We could not process your payment. Please try again or use a
                different payment method.
              </p>

              {/* Error Details */}
              {(errorMessage || responseCode) && (
                <div className="inline-flex flex-col items-center gap-1 px-4 py-3 bg-red-50 border border-red-100 mb-6">
                  <span className="text-sm font-medium text-red-800">
                    {errorMessage || getErrorDescription(responseCode)}
                  </span>
                  {responseCode && (
                    <span className="text-xs text-red-600">
                      Error code: {responseCode}
                    </span>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
                {shortCode && (
                  <Link to={`/p/${shortCode}`}>
                    <Button variant="copper" size="lg">
                      Try Again
                    </Button>
                  </Link>
                )}
                <a href="mailto:reservas@nestrentalapartments.com">
                  <Button variant="secondary" size="lg">
                    Contact Support
                  </Button>
                </a>
              </div>

              {/* Divider */}
              <div className="my-8 border-t border-neutral-200" />

              {/* Common Issues */}
              <div className="text-left max-w-md mx-auto">
                <h4 className="text-neutral-900 mb-3">
                  Common reasons for payment failure:
                </h4>
                <ul className="space-y-2 text-sm text-neutral-600">
                  <li className="flex items-start gap-2">
                    <span className="text-neutral-400">&bull;</span>
                    <span>Insufficient funds in your account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neutral-400">&bull;</span>
                    <span>Card has expired or is not activated for online purchases</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neutral-400">&bull;</span>
                    <span>Daily transaction limit exceeded</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neutral-400">&bull;</span>
                    <span>Incorrect card details entered</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Contact */}
          <div className="mt-6 text-center text-sm text-neutral-500">
            <p>
              Need assistance?{' '}
              <a
                href="mailto:reservas@nestrentalapartments.com"
                className="text-neutral-900 hover:underline"
              >
                Contact our support team
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
